import type { FC, ReactElement } from 'react';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from '../../../messages';
import { SearchDialogProduct, type TabRendererArgs } from '../../product-router';

import { type ExtensibleTabProps } from '../extensible-tab-types';
import {
	CONFLUENCE_PRODUCT_ID,
	type ConfluenceScope,
	type ConfluenceRecentsClientConfig,
	type SupportedConfScopeResponses,
} from './types';

import { defaultPermissionsSupplier } from './permission-supplier';
import { useConfluenceSections } from './sections';

import { type RendererOverrideTypes } from '../confluence/types';
import { ConfluenceAvailabilityExperimentTab } from './confluence-availability-in-jira-search/confluence-availability-experiment-tab';
import { ConfluenceAvailabilityTabBody } from './confluence-availability-in-jira-search/confluence-availability-tab-body';
import { useDefaultSuppliers } from '../../result-supplier';
import { useGenericFilters } from '../../filters';
import { ProductStates } from '../../product-state-machine/product-state-machine-types';

export interface ConfluenceUpsellInJiraTabProps extends ExtensibleTabProps {
	recentClientConfig: ConfluenceRecentsClientConfig;
	formatDate?: (lastModified: string) => ReactElement;
	autocorrectEnabled?: boolean;
	additionalContentTypesEnabled?: boolean;
	rendererOverride?: RendererOverrideTypes;
	confluenceAvailabilityExperiment?: {
		isEligible: boolean;
		isTabClicked: boolean;
		setAllowConfluenceAccessCache: () => void;
	};
}

export const ConfluenceUpsellInJiraTab: FC<ConfluenceUpsellInJiraTabProps> = ({
	recentClientConfig,
	formatDate,
	children,
	autocorrectEnabled,
	additionalContentTypesEnabled = false,
	rendererOverride,
	confluenceAvailabilityExperiment,
	...rest
}) => {
	const { defineFilters } = useGenericFilters();
	const { formatMessage } = useIntl();

	const initializeFilters = useCallback(() => {
		defineFilters(CONFLUENCE_PRODUCT_ID, []);
	}, [defineFilters]);

	initializeFilters();

	const sections = useConfluenceSections({
		formatDate,
		autocorrectEnabled,
		additionalContentTypesEnabled,
		rendererOverride,
	});

	const itemSuppliers = useDefaultSuppliers<SupportedConfScopeResponses, ConfluenceScope | string>(
		CONFLUENCE_PRODUCT_ID,
		sections,
		{
			preQueryItemSupplier: undefined,
			postQueryItemSupplier: undefined,
		},
	);

	const { isTabClicked: isConfluenceAvailabilityTabClicked, setAllowConfluenceAccessCache } =
		confluenceAvailabilityExperiment ?? {
			isTabClicked: false,
		};

	const confluenceAvailabilityExperimentTabRenderer = useCallback(
		({ title }: TabRendererArgs) => (
			<ConfluenceAvailabilityExperimentTab
				title={title}
				isTabClicked={isConfluenceAvailabilityTabClicked}
			/>
		),
		[isConfluenceAvailabilityTabClicked],
	);

	return (
		<SearchDialogProduct
			id={CONFLUENCE_PRODUCT_ID}
			title={formatMessage(messages.confluence_tab_label)}
			tabRenderer={confluenceAvailabilityExperimentTabRenderer}
			sections={sections}
			{...rest}
			{...itemSuppliers}
			permissionSupplier={defaultPermissionsSupplier}
		>
			{({ productState }) => {
				//Force set confluence access cache to true if the product state is PreQuerySuccess
				//This is specifically for the case where the user has access to confluence but the confluence access cache is not updated.
				if (productState === ProductStates.PreQuerySuccess) {
					setAllowConfluenceAccessCache?.();
				}

				return {
					Body: () => <ConfluenceAvailabilityTabBody confluenceUrl={recentClientConfig.url} />,
					Footer: () => <></>,
				};
			}}
		</SearchDialogProduct>
	);
};
